import { isEmpty } from 'lodash-es';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { PrivateListingStorage } from 'client/site-modules/private-listing/utils/storage';
import { STORAGE_KEYS } from 'client/site-modules/private-listing/constants/private-listing';
import { withMetrics } from 'client/data/api/api-metrics';
import { EdmundsAPI, PrivatePartyAPI } from 'client/data/api/api-client';
import { logger } from 'client/utils/logger/logger';
import { buildAllTmvBandsUrl } from 'client/data/models/vehicle';
import { makePrequalifyRequest, modsRecordPath, MultiOfferModel } from 'client/data/models/multi-offer';
import { buildOptionWithOemCodeStylePath, buildVehicleStylePath, VehicleModel } from 'client/data/models/vehicle-v2';
import { VisitorModel } from 'client/data/models/visitor';
import { getFormattedVehicleData } from 'site-modules/private-listing/utils/steps-utils';
import { transformColorsData } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/colors-and-options-step/colors-and-options-step-utils';
import { getQuery } from 'client/utils/location';
import { DMA, ZIP_CODE } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { getSearchResultsQuery } from 'site-modules/shared/utils/inventory/params-conversion';

export const MODEL = {
  LAST_COMPLETED_STEP: STORAGE_KEYS.LAST_COMPLETED_STEP,
  IS_SUBMITTED_LISTING: STORAGE_KEYS.IS_SUBMITTED_LISTING,
  IS_UPDATED_LISTING: STORAGE_KEYS.IS_UPDATED_LISTING,
  SHOW_NOT_OWNER_ERROR_MSG: STORAGE_KEYS.SHOW_NOT_OWNER_ERROR_MSG,
  VEHICLE: STORAGE_KEYS.VEHICLE,
  TMV_DATA: STORAGE_KEYS.TMV_DATA,
  TMV_DATA_BY_MODS_ID: 'tmvData["{modsId}"]',
  LISTING_DATA: 'listingData["{listingId}"]',
  OFFER_DATA: 'offerData["{modsId}"]',
  PREQUALIFICATION:
    'stateCode["{stateCode}"].dma["{dma}"].year["{year}"].mileage["{mileage}"].makeSlug["{makeSlug}"].prequalification',
  INVENTORIES: 'inventories',
};

export const PATHS = {
  LAST_COMPLETED_STEP: 'lastCompletedStep',
  IS_SUBMITTED_LISTING: 'isSubmittedListing',
  IS_UPDATED_LISTING: 'isUpdatedListing',
  SHOW_NOT_OWNER_ERROR_MSG: 'showNotOwnerError',
  VEHICLE: 'vehicle',
  TMV_DATA: 'tmvData',
  TMV_DATA_BY_MODS_ID: ({ modsId }) => `tmvData["${modsId}"]`,
  OFFER_DATA: ({ modsId }) => `offerData["${modsId}"]`,
  LISTING_DATA: ({ listingId }) => `listingData["${listingId}"]`,
  PREQUALIFICATION: ({ stateCode, dma, year, mileage, makeSlug }) =>
    `stateCode["${stateCode}"].dma["${dma}"].year["${year}"].mileage["${mileage}"].makeSlug["${makeSlug}"].prequalification`,
  INVENTORIES: 'inventories',
};

export const buildTmvDataPath = location => {
  const modsId = getQuery(location)?.modsId;

  return modsId ? PATHS.TMV_DATA_BY_MODS_ID({ modsId }) : PATHS.TMV_DATA;
};

export async function getTmvData(params, context) {
  let response = {};
  const tmvApiUrl = buildAllTmvBandsUrl({
    ...params,
    optionIds: params.tmvOptionCodes,
    colorId: +params?.exteriorColor?.id || undefined,
  });

  if (!tmvApiUrl) return context.abort();

  try {
    response = await withMetrics(EdmundsAPI, context).fetchJson(tmvApiUrl, { showAPIError: true });
  } catch (ex) {
    response = {
      tmvconditions: {},
      hasError: true,
    };
    logger.error(`Used TMV API fetch error - ${ex.message || ex}`);
  }

  return { ...response };
}

export const PrivateListingModel = createModelSegment('privateListing', [
  {
    path: MODEL.LAST_COMPLETED_STEP,
    async resolve() {
      return PrivateListingStorage.getValueFromSessionStorage(STORAGE_KEYS.LAST_COMPLETED_STEP) || null;
    },
    async update(data) {
      PrivateListingStorage.setValueToSessionStorage(STORAGE_KEYS.LAST_COMPLETED_STEP, data);
      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.IS_SUBMITTED_LISTING,
    async resolve() {
      return PrivateListingStorage.getValueFromSessionStorage(STORAGE_KEYS.IS_SUBMITTED_LISTING) || false;
    },
    async update(data) {
      PrivateListingStorage.setValueToSessionStorage(STORAGE_KEYS.IS_SUBMITTED_LISTING, data);
      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.IS_UPDATED_LISTING,
    async resolve() {
      return PrivateListingStorage.getValueFromSessionStorage(STORAGE_KEYS.IS_UPDATED_LISTING) || false;
    },
    async update(data) {
      PrivateListingStorage.setValueToSessionStorage(STORAGE_KEYS.IS_UPDATED_LISTING, data);
      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.SHOW_NOT_OWNER_ERROR_MSG,
    async resolve() {
      return PrivateListingStorage.getValueFromSessionStorage(STORAGE_KEYS.SHOW_NOT_OWNER_ERROR_MSG) || false;
    },
    async update(data) {
      PrivateListingStorage.setValueToSessionStorage(STORAGE_KEYS.SHOW_NOT_OWNER_ERROR_MSG, data);
      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.VEHICLE,
    async resolve() {
      return PrivateListingStorage.getValueFromSessionStorage(STORAGE_KEYS.VEHICLE) || {};
    },
    async update(data) {
      const dataToSave = isEmpty(data)
        ? data
        : {
            ...PrivateListingStorage.getValueFromSessionStorage(STORAGE_KEYS.VEHICLE),
            ...data,
          };

      PrivateListingStorage.setValueToSessionStorage(STORAGE_KEYS.VEHICLE, { ...dataToSave });

      return Promise.resolve(dataToSave);
    },
  },
  {
    path: MODEL.TMV_DATA,
    async resolve(params, context) {
      const allParams = await context.resolveValue('vehicle');

      if (!allParams) return {};
      return getTmvData(allParams, context);
    },
  },
  {
    path: MODEL.TMV_DATA_BY_MODS_ID,
    async resolve({ modsId }, context) {
      const allParams = await context.resolveValue(PATHS.OFFER_DATA({ modsId }));

      if (!allParams) return {};
      return getTmvData(allParams, context);
    },
  },
  {
    path: MODEL.OFFER_DATA,
    async resolve({ modsId }, context) {
      const offerData = await context.resolveValue(modsRecordPath.build(modsId), MultiOfferModel);

      const styleId = offerData?.vehicle?.edmundsStyleId;
      let style = {};

      if (styleId) {
        const [styleData, styleFeatures] = await Promise.all([
          context.resolveValue(buildVehicleStylePath({ styleId }), VehicleModel),
          context.resolveValue(buildOptionWithOemCodeStylePath(styleId), VehicleModel),
        ]);

        style = { ...styleData, ...transformColorsData(styleFeatures) };
      }

      return offerData?.vehicle ? { ...getFormattedVehicleData(offerData, style) } : {};
    },
  },

  {
    path: MODEL.LISTING_DATA,
    async resolve({ listingId }) {
      try {
        const response = await PrivatePartyAPI.fetchJson(`/listings?listingId=${listingId}`, {
          method: 'GET',
          headers: { 'content-type': 'application/json' },
          showAPIError: true,
        });

        return response;
      } catch (e) {
        logger.error(`Listings API fetch error - ${e.message || e}`);
        return {};
      }
    },
  },
  {
    path: MODEL.PREQUALIFICATION,
    async resolve({ stateCode, dma, year, mileage, makeSlug }, context) {
      return makePrequalifyRequest(stateCode, dma, year, mileage, makeSlug, context);
    },
  },
  {
    path: MODEL.INVENTORIES,
    async resolve(_, context) {
      const apiPath = '/purchasefunnel/v1/srp/inventory';
      const options = { timeout: 1000 };

      const { zipCode, dma, latitude, longitude, stateCode } = await context.resolveValue(
        'location',
        VisitorModel,
        false // we need prevent update location here, because we do update manually from the page component
      );

      const addLatLongParams = !!latitude && !!longitude;
      const latLongQueryParams = addLatLongParams ? { lat: latitude, lon: longitude } : {};
      const defaultRadiusParam = dma ? { [DMA]: dma } : {};
      const locationQueryParams = { [ZIP_CODE]: zipCode, ...defaultRadiusParam, ...latLongQueryParams };

      const query = getSearchResultsQuery({
        ...locationQueryParams,
        pageSize: 4,
        stateCode,
        inventoryType: ['used'],
        sellerType: 'Private Seller',
        fetchSuggestedFacets: false,
        fetchFacets: false,
        seoInfo: false,
      });

      const res = await withMetrics(EdmundsAPI, context).fetchJson(`${apiPath}${query ? `?${query}` : ''}`, options);

      return res?.inventories?.results ?? [];
    },
  },
]);
